import React from 'react'
import styled from 'styled-components'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import Modal, { CloseButton } from '@jarvis.oddle/core/components/Modal'
import { H4 } from '@jarvis.oddle/core/components/Typo'
import AddressSearch from 'src/components/molecules/AddressSearch'
import Form from '@jarvis.oddle/core/components/Form'
import { PortalDest } from '@jarvis.oddle/core/components/Portal'
import { getAddressError } from '@jarvis.oddle/core/tools/validation'
import useAppSelector from 'src/hooks/useAppSelector'
import { selectDetailMenuSite } from 'src/redux/meta'
import { makeControlledModal } from 'src/decorators/ModalController'
import { isObjectEmpty } from 'src/utils/object'

const StyledModalSection = styled(Modal.Section)`
  && {
    display: flex;
    flex-grow: 1;
    flex-shrink: 0;
    padding-bottom: 0;
  }
`

const AddressEditorModal = ({
  modal,
  onSave,
  address
}: {
  modal: {
    show: boolean
    onClose: () => void
    onClosed: () => void
  }
  onSave: (address) => Promise<{
    meta: {
      requestStatus: string
    }
    payload
    error
  }>
  address: DeliveryAddresses
}) => {
  const { t } = React.useContext(TranslationContext)
  const meta = useAppSelector(selectDetailMenuSite)

  const onSubmit = async (values, actions) => {
    const { deliveryAddress, addressTwo } = values
    const response = await onSave({
      autoCompleteAddress: deliveryAddress.formattedAddress,
      address: deliveryAddress.addressOne,
      addressTwo,
      city: deliveryAddress.city,
      state: deliveryAddress.state,
      country: meta.menuCountry,
      postal: deliveryAddress.postal,
      latitude: deliveryAddress.latitude,
      longitude: deliveryAddress.longitude
    })
    if (response.meta.requestStatus === 'fulfilled' && isObjectEmpty(response.payload.error)) {
      modal.onClose()
    } else {
      actions.setFieldError('general', JSON.stringify(response.payload.error.message))
      actions.setSubmitting(false)
    }
  }

  const onValidate = ({ deliveryAddress }) => {
    const errors = {} as {
      deliveryAddress?: string
    }

    const addressError = getAddressError(t, deliveryAddress)
    if (addressError) errors.deliveryAddress = addressError

    return errors
  }

  return (
    <Modal {...modal} initialFocus='#address'>
      <Form
        initialValues={{ deliveryAddress: address }}
        onValidate={onValidate}
        onSubmit={onSubmit}
        disableAutocomplete>
        <Modal.FlexContainer>
          <Modal.Header>
            <H4 margin='none' bold>{t('New Address')}</H4>
            <CloseButton onClick={modal.onClose} />
          </Modal.Header>
          <Modal.Body>
            <StyledModalSection>
              <Form.ErrorMessage>
                {({ errors }) => errors.general ? t(errors.general) : null}
              </Form.ErrorMessage>
              <AddressSearch submitButtonText={t('Save')} />
            </StyledModalSection>
          </Modal.Body>
          <Modal.Footer>
            <PortalDest channel='AddressSearchSubmitButton' />
          </Modal.Footer>
        </Modal.FlexContainer>
      </Form>
    </Modal>
  )
}

export default makeControlledModal(AddressEditorModal, 'AddressEditorModal')
